var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { datadogLogs } from '@datadog/browser-logs';
import dayjs from 'dayjs';
import { IrisClient } from '@pinwheel/trackjs';
import { v4 } from 'uuid';
import { DOCUMENT_UPLOADS_EVENT_SOURCES, ERROR_EVENT_SOURCES, EventSurface, EventType, GUIDED_SWITCH_EVENT_SOURCES, INTRO_EVENT_SOURCES, LOGIN_EVENT_SOURCES, MANUAL_INSTRUCTIONS_EVENT_SOURCES, POST_LOGIN_EVENT_SOURCES, PROVIDER_CONFIRMATION_EVENT_SOURCES, SEARCH_EVENT_SOURCES, PARTNER_PLATFORM_ELIGIBILITY_EVENT_SOURCES, } from '../types/events';
import { getSemverString, getWithPath, getWriteJobFromToken, isStaging, } from '../utils';
import { IRIS_FLUSH_INTERVAL } from '../consts/polling';
var MAX_METADATA_MODEL_LENGTH = 100;
export var irisClient = new IrisClient({
    flushInterval: IRIS_FLUSH_INTERVAL,
    env: isStaging() ? 'staging' : 'production',
    warnlog: function (s, o) { return datadogLogs.logger.warn(s, o); },
    analyticsKey: isStaging()
        ? '3c8746cd-4ca4-49a5-899a-76c069ce7842'
        : 'dc9090a7-d699-47d7-9360-f2682e80afe6',
    service: 'link',
});
export var irisEvent = function (eventName, properties) {
    var messageId = v4();
    var payload = {
        anonymousId: properties.modal_session_id,
        event: eventName,
        userId: properties.unique_user_id,
        properties: properties,
        messageId: messageId,
    };
    irisClient.track(payload);
};
export var irisTrack = function (event, properties) {
    // Soon to be Deprecated! Use irisEvent for unified logging
    var messageId = v4();
    var payload = {
        anonymousId: properties === null || properties === void 0 ? void 0 : properties.modalSessionId,
        event: EventType.LINK_EVENT,
        properties: properties,
        messageId: messageId,
    };
    irisClient.track(payload);
};
var getAllProperties = function (properties, modalSessionId, selectedEmployer, uniqueUserId, platform) {
    var obj = __assign(__assign({}, properties), { modalSessionId: modalSessionId });
    if (uniqueUserId)
        obj.uniqueUserId = uniqueUserId;
    if (selectedEmployer)
        Object.assign(obj, {
            selectedEmployerId: selectedEmployer.id,
            selectedEmployerName: selectedEmployer.name,
        });
    if (platform)
        Object.assign(obj, {
            platformId: platform.id,
            // eslint-disable-next-line dot-notation
            platformKey: platform['key'],
        });
    return obj;
};
// This is the new event logging function referred to as unified event logging because
// we are using consistent fields in this new paradigm for all services. It's also
// unified in that we use it to log to both Datadog and Iris simultaneously and
// is used for both page visit and page action events which were separate in the
// deprecated model.
export var getUnifiedEventFn = function (linkTokenPayload, modalSessionId, linkInitOpts) {
    return function (eventName, payload) {
        var _a;
        var uniqueUserId = linkInitOpts.uniqueUserId, version = linkInitOpts.version, platform = linkInitOpts.platform, sdk = linkInitOpts.sdk, deviceMetadata = linkInitOpts.deviceMetadata, experiments = linkInitOpts.experiments, featureFlags = linkInitOpts.featureFlags, localStorageAvailable = linkInitOpts.localStorageAvailable;
        var orgName = linkTokenPayload.orgName, tokenId = linkTokenPayload.tokenId, apiKey = linkTokenPayload.apiKey, _uid = linkTokenPayload._uid, requiredJobs = linkTokenPayload.requiredJobs, mode = linkTokenPayload.mode, isAppClip = linkTokenPayload.isAppClip;
        var truncatedDeviceMetadataModel = deviceMetadata
            ? __assign(__assign({}, deviceMetadata), { 
                // We are limiting the max size of device metadata model because sometimes when device is unknown,
                // the value is populated with a really long unhelpful string starting with `utsname(sysname: (0, 0, 0, 0, ...`
                model: deviceMetadata.model
                    ? deviceMetadata.model.substring(0, MAX_METADATA_MODEL_LENGTH)
                    : undefined }) : undefined;
        var canonicalFields = {
            org_name: orgName,
            api_key: apiKey,
            modal_session_id: modalSessionId,
            unique_user_id: uniqueUserId,
            link_token_id: tokenId,
            job: getWriteJobFromToken(linkTokenPayload) || requiredJobs[0],
            required_jobs: requiredJobs,
            mode: mode,
            event_time: Date.now(),
            device_platform: platform,
            sdk: sdk,
            device_metadata: truncatedDeviceMetadataModel,
            sdk_version: version,
            unified_event_type: eventName,
            customer_user_id: _uid,
            statsigCustomIDs: ['workspace_name', linkTokenPayload.workspaceName],
            localStorageAvailable: localStorageAvailable,
            client_timestamp: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
            browser_language: (_a = window === null || window === void 0 ? void 0 : window.navigator) === null || _a === void 0 ? void 0 : _a.language,
            dynamic_loading_screen_test_type: experiments.dynamicLoadingScreen,
            is_app_clip: isAppClip,
        };
        // Only include feature flags as separate columns for the LINK_FF_EVENT
        var combinedFields = __assign(__assign(__assign({}, canonicalFields), payload), { statsigFeatureFlags: eventName === EventType.LINK_FF_EVENT ? featureFlags : undefined });
        var logObject = __assign(__assign({}, combinedFields), { unified_event_fields: combinedFields });
        datadogLogs.logger.info("Unified Event: ".concat(eventName), logObject);
        irisEvent(eventName, combinedFields);
    };
};
// TODO: Deprecate this once we no longer depend on the old event logging.
var getMinutesSinceTokenIat = function () {
    var iat = getWithPath(datadogLogs.getLoggerGlobalContext(), [
        'metadata',
        'context',
        'tokenParams',
        'iat',
    ]);
    return Math.floor((Date.now() - iat * 1000) / 1000 / 60);
};
export var getFireEventFn = function (irisFn, linkTokenPayload, modalSessionId, selectedEmployer, linkInitOpts, platform, checkpoints) {
    return function (event, properties, isPagelessTrackEvent) {
        if (properties === void 0) { properties = {}; }
        if (isPagelessTrackEvent === void 0) { isPagelessTrackEvent = false; }
        var uniqueUserId = linkInitOpts.uniqueUserId, version = linkInitOpts.version;
        var checkpointsObj = Array.from(checkpoints).reduce(function (current, item) {
            current[item] = true;
            return current;
        }, {});
        var propertiesPlus = getAllProperties(__assign(__assign(__assign({}, properties), linkTokenPayload), checkpointsObj), modalSessionId, selectedEmployer, uniqueUserId, platform);
        Object.assign(propertiesPlus, {
            minutesSinceTokenInstantiation: getMinutesSinceTokenIat(),
        });
        if (platform)
            Object.assign(propertiesPlus, { platform: platform });
        if (isPagelessTrackEvent) {
            var _a = event.split(' '), actionName = _a[1];
            Object.assign(propertiesPlus, { actionName: actionName });
        }
        else {
            var _b = event.split(' '), pageName = _b[1], actionName = _b[2];
            Object.assign(propertiesPlus, { pageName: pageName });
            if (actionName)
                Object.assign(propertiesPlus, { actionName: actionName });
        }
        var metaProperties = datadogLogs.getLoggerGlobalContext()
            ? {
                meta: {
                    sdk: datadogLogs.getLoggerGlobalContext().sdk,
                    platform: datadogLogs.getLoggerGlobalContext().platform,
                    version: getSemverString(version),
                },
            }
            : undefined;
        datadogLogs.logger.info(event, propertiesPlus);
        irisFn(event, __assign(__assign({}, propertiesPlus), metaProperties));
    };
};
export var getPostLoginSource = function (token) {
    if (getWriteJobFromToken(token) === 'direct_deposit_switch') {
        return token.amount
            ? EventSurface.DD_PAYMENT
            : EventSurface.DD_AMOUNT_SELECTION;
    }
    return EventSurface.DD_PAYMENT;
};
export var getErrorSource = function (lastSource) {
    if (ERROR_EVENT_SOURCES.includes(lastSource)) {
        return lastSource;
    }
    if (SEARCH_EVENT_SOURCES.includes(lastSource)) {
        return EventSurface.SEARCH_ERROR;
    }
    if (LOGIN_EVENT_SOURCES.includes(lastSource)) {
        switch (lastSource) {
            case EventSurface.ACCOUNT_RECOVERY:
                return EventSurface.ACCOUNT_RECOVERY_ERROR;
            case EventSurface.REAUTH:
                return EventSurface.REAUTH_ERROR;
            default:
                return EventSurface.LOGIN_ERROR;
        }
    }
    if (POST_LOGIN_EVENT_SOURCES.includes(lastSource)) {
        return EventSurface.JOB_ERROR;
    }
    return undefined;
};
// A helper method which fires the proper event type given the source.
export var getFireProperEventTypeFn = function (fireUnifiedEvent) {
    return function (action, opts, optionalEventParams) {
        var _a, _b, _c;
        var entry_point = opts.lastSource, source = opts.currentSource, search_session_id = opts.searchSessionId, login_attempt_id = opts.loginAttemptId, post_login_attempt_id = opts.postLoginAttemptId, manual_instructions_attempt_id = opts.manualInstructionsAttemptId;
        var _d = optionalEventParams || {}, search_text = _d.searchText, viewport_results = _d.viewportResults, ddAmountOverride = _d.ddAmountOverride, employerIdOverride = _d.employerIdOverride, platformIdOverride = _d.platformIdOverride, tab = _d.tab, event_id = _d.eventId, file_size = _d.fileSize, file_type = _d.fileType, matched_platforms = _d.matchedPlatforms, is_background_eligibility_check = _d.isBackgroundEligibilityCheck;
        var employer_id = employerIdOverride || ((_a = opts.selectedEmployer) === null || _a === void 0 ? void 0 : _a.id);
        var platform_id = platformIdOverride || ((_b = opts.loadedPlatform) === null || _b === void 0 ? void 0 : _b.id);
        var amount = (_c = opts.linkParams) === null || _c === void 0 ? void 0 : _c.amount;
        var dd_amount = ddAmountOverride ||
            ((amount === null || amount === void 0 ? void 0 : amount.unit) === '$' ? amount.value * 100 : undefined);
        // Exit confirmation is the only surface which doesn't have a corresponding event type
        // and thus we need to determine the event type to fire based on the entry point.
        var typeDeterminingSource = source === EventSurface.EXIT_CONFIRMATION ||
            source === EventSurface.EXIT_CONFIRMATION_ALT
            ? entry_point
            : source;
        if (INTRO_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_INTRO_EVENT, {
                entry_point: entry_point,
                action: action,
                source: source,
            });
            return;
        }
        if (MANUAL_INSTRUCTIONS_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.MANUAL_INSTRUCTIONS_EVENT, {
                manual_instructions_attempt_id: manual_instructions_attempt_id,
                entry_point: entry_point,
                action: action,
                source: source,
                employer_id: employer_id,
                platform_id: platform_id,
            });
            return;
        }
        if (PROVIDER_CONFIRMATION_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_PROVIDER_CONFIRMATION_EVENT, {
                entry_point: entry_point,
                action: action,
                source: source,
                platform_id: platform_id,
                employer_id: employer_id,
            });
        }
        if (SEARCH_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_SEARCH_EVENT, {
                search_session_id: search_session_id,
                employer_id: employer_id,
                platform_id: platform_id,
                entry_point: entry_point,
                action: action,
                source: source,
                search_text: search_text,
                viewport_results: viewport_results,
                tab: tab,
            });
        }
        if (LOGIN_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_LOGIN_EVENT, {
                login_attempt_id: login_attempt_id,
                search_session_id: search_session_id,
                employer_id: employer_id,
                platform_id: platform_id,
                entry_point: entry_point,
                action: action,
                source: source,
                event_id: event_id,
            });
        }
        if (POST_LOGIN_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_POST_LOGIN_EVENT, {
                post_login_attempt_id: post_login_attempt_id,
                employer_id: employer_id,
                platform_id: platform_id,
                entry_point: entry_point,
                action: action,
                source: source,
                dd_amount: dd_amount,
            });
        }
        if (PARTNER_PLATFORM_ELIGIBILITY_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_PARTNER_ELIGIBILITY_EVENT, {
                post_login_attempt_id: post_login_attempt_id,
                employer_id: employer_id,
                platform_id: platform_id,
                entry_point: entry_point,
                action: action,
                source: source,
                dd_amount: dd_amount,
                matched_platforms: matched_platforms,
                is_background_eligibility_check: is_background_eligibility_check,
            });
        }
        if (DOCUMENT_UPLOADS_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_DOCUMENT_UPLOADS_EVENT, {
                entry_point: entry_point,
                action: action,
                source: source,
                file_size: file_size,
                file_type: file_type,
            });
        }
        if (GUIDED_SWITCH_EVENT_SOURCES.includes(typeDeterminingSource)) {
            fireUnifiedEvent(EventType.LINK_GUIDED_SWITCH_EVENT, {
                employer_id: employer_id,
                platform_id: platform_id,
                entry_point: entry_point,
                action: action,
                source: source,
                dd_amount: dd_amount,
            });
        }
    };
};
